// Per-seat seat form (CIN-15126): the style, edge softness, pose and details a seat is drawn with. Every field can be
// set on a seat or a category (`design.seatMaterials`); a field nobody set is the room default (the design's
// `seatStyle`, `seatRoundness`, `seats` and `seatExtras`, which no editor shows any more). One rule for the studio and
// the guest map. Pure; relative imports only.
import {
    DEFAULT_SEAT_ROUNDNESS,
    MAX_SEAT_FORMS,
    type RoomDesign,
    type SeatFormSpec,
    type SeatMaterials
} from './room-design.ts';
import { type MaterialSeat, seatMaterialKey } from './seat-materials.ts';
import type { SeatLook } from './seat-styles.ts';

/** A seat's form with every field decided. */
export type SeatForm = Required<SeatFormSpec>;

/** The fields of a form, in the order they are saved and compared. */
export const FORM_FIELDS = [
    'style',
    'roundness',
    'pose',
    'twoTone',
    'headrestNumbers',
    'endPanels',
    'cupholders',
    'trayTables'
] as const satisfies readonly (keyof SeatForm)[];
export type FormField = (typeof FORM_FIELDS)[number];

/** What the room default is made of. Tolerant of a hand-built or very old document that skipped the normalizer. */
export type RoomFormDesign = Partial<Pick<RoomDesign, 'seatStyle' | 'seatRoundness' | 'seats' | 'seatExtras'>>;

/** The form of a seat nobody set anything on: the design's room-level seat keys. */
export function roomSeatForm(design: RoomFormDesign): SeatForm {
    return {
        style: design.seatStyle ?? 'classic',
        roundness: design.seatRoundness ?? DEFAULT_SEAT_ROUNDNESS,
        pose: design.seats?.pose ?? 'upright',
        twoTone: design.seats?.twoTone ?? false,
        headrestNumbers: design.seats?.headrestNumbers ?? false,
        endPanels: design.seats?.endPanels ?? false,
        cupholders: design.seatExtras?.cupholders ?? true,
        trayTables: design.seatExtras?.trayTables ?? false
    };
}

/** The room-level keys of a design for a form: what `roomSeatForm` reads back. */
export function roomKeysOf(form: SeatForm): Pick<RoomDesign, 'seatStyle' | 'seatRoundness' | 'seats' | 'seatExtras'> {
    return {
        seatStyle: form.style,
        seatRoundness: form.roundness,
        seats: {
            pose: form.pose,
            twoTone: form.twoTone,
            headrestNumbers: form.headrestNumbers,
            endPanels: form.endPanels
        },
        seatExtras: { trayTables: form.trayTables, cupholders: form.cupholders }
    };
}

/** Whether a field decides how a seat of that form looks; pose, cupholders and tray tables are the recliner's. */
export function formFieldApplies(field: FormField, style: SeatForm['style']): boolean {
    return style === 'recliner' || (field !== 'pose' && field !== 'cupholders' && field !== 'trayTables');
}

/**
 * A string that is equal for two forms exactly when they draw the same seat: fields that do not apply to the style
 * are left out, and the number plate is not geometry, so it is left out too (`fullFormKey` has it).
 */
export function geometryKey(form: SeatForm): string {
    const recliner = form.style === 'recliner';
    return [
        form.style,
        form.roundness,
        recliner ? form.pose : '-',
        form.twoTone ? 't' : '-',
        form.endPanels ? 'e' : '-',
        recliner && form.cupholders ? 'c' : '-',
        recliner && form.trayTables ? 'y' : '-'
    ].join('|');
}

/** `geometryKey` plus the number plate: two forms are the same look exactly when their full keys are. */
export const fullFormKey = (form: SeatForm): string => `${geometryKey(form)}|${form.headrestNumbers ? 'n' : '-'}`;

/** What the resolver needs of the design. */
export type FormResolveDesign = RoomFormDesign & { seatMaterials?: SeatMaterials };

/** Seat entry first, then the category's, then the room default, field by field. */
export function resolveSeatForm(design: FormResolveDesign, seat: MaterialSeat, room = roomSeatForm(design)): SeatForm {
    const materials = design.seatMaterials;
    if (!materials) return room;
    const ownKey = seatMaterialKey(seat);
    const own = ownKey === null ? undefined : materials.bySeat[ownKey];
    const category =
        seat.categoryId === null || seat.categoryId === undefined
            ? undefined
            : materials.byCategory[String(seat.categoryId)];
    if (!own && !category) return room;
    return {
        style: own?.style ?? category?.style ?? room.style,
        roundness: own?.roundness ?? category?.roundness ?? room.roundness,
        pose: own?.pose ?? category?.pose ?? room.pose,
        twoTone: own?.twoTone ?? category?.twoTone ?? room.twoTone,
        headrestNumbers: own?.headrestNumbers ?? category?.headrestNumbers ?? room.headrestNumbers,
        endPanels: own?.endPanels ?? category?.endPanels ?? room.endPanels,
        cupholders: own?.cupholders ?? category?.cupholders ?? room.cupholders,
        trayTables: own?.trayTables ?? category?.trayTables ?? room.trayTables
    };
}

const hasFormField = (spec: SeatFormSpec): boolean => FORM_FIELDS.some((field) => spec[field] !== undefined);
const overrideCache = new WeakMap<SeatMaterials, boolean>();

/** Whether any seat or category entry sets a form field; false for every document from before the seat forms. */
export function hasFormOverrides(materials: SeatMaterials): boolean {
    let hit = overrideCache.get(materials);
    if (hit === undefined) {
        hit =
            Object.values(materials.bySeat).some(hasFormField) ||
            Object.values(materials.byCategory).some(hasFormField);
        overrideCache.set(materials, hit);
    }
    return hit;
}

/** Whether a category entry sets a form field, so a seat's category decides its form. */
export const hasCategoryForms = (materials: SeatMaterials | undefined): boolean =>
    materials !== undefined && Object.values(materials.byCategory).some(hasFormField);

export type SeatForms<K> = {
    /** The room default: the form of every seat that is not in `bySeat`. */
    room: SeatForm;
    /** The forms that differ from the room default, keyed by `keyOf(seat)`. */
    bySeat: Map<K, SeatForm>;
    /** Seats that asked for a form beyond the cap and were drawn in the room default. */
    overflow: number;
};

/**
 * The form of every seat that differs from the room default. A room shows at most `maxForms` distinct forms (the
 * room default counts as one): the least used ones beyond that fall back to the room default, so a room where every
 * seat is different still builds a bounded set of seat models.
 */
export function resolveSeatForms<S extends MaterialSeat, K>(
    design: FormResolveDesign,
    seats: readonly S[],
    keyOf: (seat: S) => K,
    maxForms = MAX_SEAT_FORMS
): SeatForms<K> {
    const room = roomSeatForm(design);
    const bySeat = new Map<K, SeatForm>();
    if (!design.seatMaterials || !hasFormOverrides(design.seatMaterials)) return { room, bySeat, overflow: 0 };
    const roomKey = fullFormKey(room);
    const shared = new Map<string, SeatForm>();
    const keyOfSeat = new Map<K, string>();
    const uses = new Map<string, number>();
    for (const seat of seats) {
        const form = resolveSeatForm(design, seat, room);
        const key = fullFormKey(form);
        if (key === roomKey) continue;
        if (!shared.has(key)) shared.set(key, form);
        keyOfSeat.set(keyOf(seat), key);
        uses.set(key, (uses.get(key) ?? 0) + 1);
    }
    const kept = new Set(
        [...uses]
            .sort((a, b) => b[1] - a[1])
            .slice(0, Math.max(0, maxForms - 1))
            .map(([key]) => key)
    );
    let overflow = 0;
    for (const [seatKey, formKey] of keyOfSeat) {
        const form = shared.get(formKey);
        if (form && kept.has(formKey)) bySeat.set(seatKey, form);
        else overflow += 1;
    }
    return { room, bySeat, overflow };
}

/** Whether two results draw every seat the same way (the geometry only: the number plate is drawn on its own). */
export function sameSeatForms<K>(a: SeatForms<K>, b: SeatForms<K>): boolean {
    if (geometryKey(a.room) !== geometryKey(b.room) || a.room.headrestNumbers !== b.room.headrestNumbers) return false;
    if (a.bySeat.size !== b.bySeat.size) return false;
    for (const [key, form] of a.bySeat) {
        const other = b.bySeat.get(key);
        if (!other || fullFormKey(other) !== fullFormKey(form)) return false;
    }
    return true;
}

/** The options the seat models take: what `seatOptionsFor` turns into the options of one seat. */
export function seatLookOfForm(form: SeatForm): SeatLook {
    return {
        pose: form.pose,
        twoTone: form.twoTone,
        endPanels: form.endPanels,
        cupholders: form.cupholders,
        trayTable: form.trayTables
    };
}

/**
 * What survives applying a theme: a theme restyles materials and, for the seats, their style (and roundness where it
 * prescribes one); the details the operator built (pose, shell, plates, panels, extras) stay with their seats.
 * Entries that hold nothing else are dropped.
 */
export function keepFormDetails(materials: SeatMaterials, keepRoundness: boolean): SeatMaterials {
    const kept = (entries: SeatMaterials['bySeat']): SeatMaterials['bySeat'] => {
        const out: SeatMaterials['bySeat'] = {};
        for (const [key, entry] of Object.entries(entries)) {
            const spec: Record<string, unknown> = {};
            for (const field of FORM_FIELDS) {
                if (entry[field] === undefined || field === 'style' || (field === 'roundness' && !keepRoundness)) {
                    continue;
                }
                spec[field] = entry[field];
            }
            if (Object.keys(spec).length > 0) out[key] = spec as SeatMaterials['bySeat'][string];
        }
        return out;
    };
    return { byCategory: kept(materials.byCategory), bySeat: kept(materials.bySeat) };
}
